import {
  type ReactElement,
  type ReactNode,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import {
  NeonModalDrawer,
  NeonButtonFooter,
  NeonButton,
  NeonCheckboxSingle,
  NeonSearch,
  NeonPagination,
  NeonLoadingIndicator,
} from '@ps-refarch-ux/neon';
import { NeonButtonTypes } from '@/constants/neon-constants';
import type { School } from '@/types/school';
import { isPosReadOnly } from '@/utils/cp-context';
import { PLANS_OF_STUDY_PAGE_SIZE_OPTIONS } from '@/constants/plan-of-study-constants';
import { schoolSelectionUnchanged } from './plan-form-utils';
import {
  activeDescriptionStyle,
  noSchoolsFoundStyle,
  planNameHeadingStyle,
  publishedActionLinkStyle,
  publishedCountTextStyle,
  publishedDescriptionStyle,
  publishedHeaderWrapStyle,
  publishedLabelStyle,
  schoolsHeaderLabelStyle,
} from './schools-drawer.styles';

const DEFAULT_SCHOOLS_DRAWER_PAGE_LENGTH = 25;

/** Smallest page-size in `PLANS_OF_STUDY_PAGE_SIZE_OPTIONS` — keep pager (per-page + results) visible when list exceeds one page at that size, even if current `pageLength` is larger than row count. */
const SCHOOLS_DRAWER_PAGINATION_THRESHOLD = 25;

export interface SchoolsDrawerBaseProps {
  /** Drawer element id. */
  id: string;
  /** Header title. */
  title: string;
  /** Plan name in body. */
  planName: string;
  /** Body description under the name. */
  description: string;
  /** Schools to list. */
  allSchools: School[];
  /** Initially selected school ids. */
  initialSelectedNids: Set<string>;
  /** Ids that cannot be turned off. */
  lockedNids?: Set<string>;
  /** `published` vs `active` layout. */
  mode?: 'published' | 'active';
  /** True while the full school list is still fetching (avoids empty-state confusion). */
  isSchoolsLoading?: boolean;
  /** Called just before onSave fires — lets the parent close the drawer and start loading. */
  onBeforeSave?: () => void;
  onClose: () => void;
  /** Shown after the school list and pagination (e.g. Publish and update — Availability). */
  bodyExtension?: ReactNode;
  /** Primary footer label (default Save). */
  primaryButtonText?: string;
  /** When false, primary stays enabled even if selection matches initial (default true). */
  disablePrimaryWhenSelectionUnchanged?: boolean;
  /** When false, hides the bold plan name line above the description. */
  showPlanNameHeading?: boolean;
  /** When true, disables the primary action regardless of selection state. */
  primaryButtonDisabled?: boolean;
  /** View-only: no bulk actions, toggles disabled, footer is Cancel only (no Save/Publish/Done). */
  readOnly?: boolean;
}

export type SchoolsDrawerProps = SchoolsDrawerBaseProps &
  (
    | { onSave: (selectedSchoolIds: string[]) => void; onPrimary?: never }
    | { onPrimary: (selectedSchoolIds: string[]) => void; onSave?: never }
  );

export function SchoolsDrawer({
  id,
  title,
  planName,
  description,
  allSchools,
  initialSelectedNids,
  lockedNids,
  mode = 'active',
  isSchoolsLoading = false,
  onBeforeSave,
  onClose,
  onSave,
  onPrimary,
  bodyExtension,
  primaryButtonText = 'Save',
  disablePrimaryWhenSelectionUnchanged = true,
  showPlanNameHeading = true,
  primaryButtonDisabled = false,
  readOnly,
}: SchoolsDrawerProps): ReactElement {
  const effectiveReadOnly = readOnly ?? isPosReadOnly();
  const isPublishedMode = mode === 'published';
  const [selectedSet, setSelectedSet] = useState<Set<string>>(() => new Set(initialSelectedNids));
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLength, setPageLength] = useState(DEFAULT_SCHOOLS_DRAWER_PAGE_LENGTH);

  const publishedSet = useMemo(
    () =>
      new Set(
        Array.from(lockedNids ?? initialSelectedNids).filter((nid) =>
          allSchools.some((school) => school.nid === nid)
        )
      ),
    [allSchools, lockedNids, initialSelectedNids]
  );

  const orderedSchools = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    // For 'active' mode: sort active schools alphabetically, then inactive schools alphabetically
    if (!isPublishedMode) {
      const active: typeof allSchools = [];
      const inactive: typeof allSchools = [];
      for (const school of allSchools) {
        if (selectedSet.has(school.nid)) {
          active.push(school);
        } else {
          inactive.push(school);
        }
      }
      active.sort((a, b) => collator.compare(a.name, b.name));
      inactive.sort((a, b) => collator.compare(a.name, b.name));
      return [...active, ...inactive];
    }
    // For 'published' mode: keep existing logic
    return [...allSchools].sort((left, right) => {
      const leftGroup = publishedSet.has(left.nid) ? 0 : 1;
      const rightGroup = publishedSet.has(right.nid) ? 0 : 1;
      if (leftGroup !== rightGroup) {
        return leftGroup - rightGroup;
      }
      return collator.compare(left.name, right.name);
    });
  }, [allSchools, isPublishedMode, publishedSet, selectedSet]);

  const filteredSchools = useMemo(() => {
    if (!search.trim()) {
      return orderedSchools;
    }
    const term = search.trim().toLowerCase();
    return orderedSchools.filter((s) => s.name.toLowerCase().includes(term));
  }, [orderedSchools, search]);

  const maxPage = useMemo(
    () => Math.max(1, Math.ceil(filteredSchools.length / pageLength)),
    [filteredSchools.length, pageLength]
  );

  useEffect(() => {
    setCurrentPage((previous) => Math.min(previous, maxPage));
  }, [maxPage]);

  const startIdx = (currentPage - 1) * pageLength;
  const pageSchools = filteredSchools.slice(startIdx, startIdx + pageLength);

  const publishedCount = publishedSet.size;

  const selectedNowCount = useMemo(() => {
    let count = 0;
    for (const nid of selectedSet) {
      if (!publishedSet.has(nid) && allSchools.some((school) => school.nid === nid)) {
        count++;
      }
    }
    return count;
  }, [allSchools, publishedSet, selectedSet]);

  const handleToggle = useCallback(
    (nid: string) => {
      if (effectiveReadOnly) {
        return;
      }
      if (lockedNids?.has(nid)) {
        return;
      }
      setSelectedSet((prev) => {
        const next = new Set(prev);
        if (next.has(nid)) {
          next.delete(nid);
        } else {
          next.add(nid);
        }
        return next;
      });
    },
    [effectiveReadOnly, lockedNids]
  );

  const handleActivateAll = useCallback(() => {
    if (effectiveReadOnly) {
      return;
    }
    const pool = isPublishedMode ? filteredSchools : allSchools;
    setSelectedSet((prev) => {
      const next = new Set(prev);
      for (const s of pool) {
        next.add(s.nid);
      }
      return next;
    });
  }, [allSchools, effectiveReadOnly, filteredSchools, isPublishedMode]);

  const handleInactivateAll = useCallback(() => {
    if (effectiveReadOnly) {
      return;
    }
    setSelectedSet((prev) => {
      const next = new Set(prev);
      for (const s of allSchools) {
        if (!lockedNids?.has(s.nid)) {
          next.delete(s.nid);
        }
      }
      return next;
    });
  }, [allSchools, effectiveReadOnly, lockedNids]);

  const handlePrimaryClick = useCallback(() => {
    if (effectiveReadOnly) {
      return;
    }
    const ids = Array.from(selectedSet).filter((nid) =>
      allSchools.some((school) => school.nid === nid)
    );
    if (onPrimary) {
      onPrimary(ids);
      return;
    }
    onBeforeSave?.();
    onSave!(ids);
  }, [allSchools, effectiveReadOnly, onBeforeSave, onPrimary, onSave, selectedSet]);

  const searchPlaceholder = isPublishedMode ? 'Search' : 'Search school name';
  const isBusy = isSchoolsLoading;
  const selectionUnchanged = schoolSelectionUnchanged(selectedSet, initialSelectedNids);
  const primaryDisabled =
    primaryButtonDisabled || isBusy || (disablePrimaryWhenSelectionUnchanged && selectionUnchanged);
  const rowToggleDisabled = effectiveReadOnly || isBusy;

  return (
    <NeonModalDrawer
      id={id}
      neonDrawerHasClosed={onClose}
      data-readonly={effectiveReadOnly ? 'true' : 'false'}
      dataSize="medium"
      dataShowCloseButton="true"
      dataShowHeaderDivider="true"
      dataShowFooterDivider="true"
      dataCloseOnBackdropClick="true"
    >
      <div data-slot="drawer-header-title">{title}</div>

      <div data-slot="drawer-body">
        {showPlanNameHeading ? <p style={planNameHeadingStyle}>{planName}</p> : null}
        <p style={isPublishedMode ? publishedDescriptionStyle : activeDescriptionStyle}>
          {description}
        </p>

        {!isPublishedMode && !effectiveReadOnly && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <NeonButton
              id={`${id}-activate-all`}
              dataType={NeonButtonTypes.Secondary}
              dataText="Activate all"
              disabled={isBusy}
              onClick={handleActivateAll}
            />
            <NeonButton
              id={`${id}-inactivate-all`}
              dataType={NeonButtonTypes.Secondary}
              dataText="Inactivate all"
              disabled={isBusy}
              onClick={handleInactivateAll}
            />
          </div>
        )}

        {isPublishedMode && (
          <div style={publishedHeaderWrapStyle}>
            <span style={schoolsHeaderLabelStyle}>Schools</span>
            <span style={publishedCountTextStyle}>
              {publishedCount} published
              {!effectiveReadOnly && selectedNowCount > 0 ? `, ${selectedNowCount} selected` : ''}
            </span>
          </div>
        )}

        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div style={{ flex: 1 }}>
            <NeonSearch
              id={`${id}-search`}
              dataPlaceholderText={searchPlaceholder}
              disabled={isBusy}
              neonSearchValueChange={(value: string) => {
                setSearch(value);
                setCurrentPage(1);
              }}
            />
          </div>
          {isPublishedMode && !effectiveReadOnly && (
            <>
              <button
                type="button"
                data-testid={`${id}-select-all-link`}
                onClick={handleActivateAll}
                disabled={isBusy}
                style={{
                  ...publishedActionLinkStyle,
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  opacity: isBusy ? 0.5 : 1,
                }}
              >
                Select all
              </button>
              <button
                type="button"
                data-testid={`${id}-clear-link`}
                onClick={handleInactivateAll}
                disabled={isBusy}
                style={{
                  ...publishedActionLinkStyle,
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  opacity: isBusy ? 0.5 : 1,
                }}
              >
                Clear
              </button>
            </>
          )}
        </div>

        {!isPublishedMode && (
          <div
            style={{
              fontWeight: 700,
              fontSize: '0.875rem',
              padding: '12px 0',
              borderBottom: '1px solid var(--neon-color-grey-200, #e8ebf0)',
            }}
          >
            School
          </div>
        )}
        <div>
          {isSchoolsLoading && (
            <div
              data-testid={`${id}-schools-loading`}
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '24px 0',
              }}
            >
              <NeonLoadingIndicator dataSize="large" />
            </div>
          )}

          {!isBusy && pageSchools.length === 0 && (
            <p style={noSchoolsFoundStyle}>No schools found</p>
          )}
          {!isBusy &&
            pageSchools.map((school) => {
              const isOn = selectedSet.has(school.nid);
              const isLocked = lockedNids?.has(school.nid) ?? false;
              const toggleDisabled = rowToggleDisabled || isLocked;
              return (
                <div
                  key={school.nid}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: isPublishedMode ? '12px 24px' : '12px 0',
                    borderBottom: '1px solid var(--neon-color-grey-200, #e8ebf0)',
                    ...(isPublishedMode && isOn
                      ? { background: 'var(--neon-color-grey-100, #f5f6f8)' }
                      : {}),
                    ...(isLocked && !effectiveReadOnly
                      ? { pointerEvents: 'none', opacity: 0.6 }
                      : {}),
                    ...(effectiveReadOnly && !isOn ? { opacity: 0.72 } : {}),
                  }}
                  data-testid={`school-row-${school.nid}`}
                >
                  <NeonCheckboxSingle
                    id={`school-toggle-${school.nid}`}
                    dataCheckType="toggle"
                    dataLabelText={school.name}
                    value={isOn}
                    disabled={toggleDisabled ? true : undefined}
                    dataIsReadOnly={effectiveReadOnly ? true : undefined}
                    onInput={toggleDisabled ? undefined : () => handleToggle(school.nid)}
                  />
                  {isPublishedMode && isOn && publishedSet.has(school.nid) && (
                    <span
                      style={publishedLabelStyle}
                      data-testid={`school-published-label-${school.nid}`}
                    >
                      Published
                    </span>
                  )}
                </div>
              );
            })}
        </div>

        {!isBusy && filteredSchools.length > SCHOOLS_DRAWER_PAGINATION_THRESHOLD && (
          <NeonPagination
            id={`${id}-pagination`}
            dataArrayLength={filteredSchools.length}
            dataCurrentPage={currentPage}
            dataPageLength={pageLength}
            dataPageSizeOptions={PLANS_OF_STUDY_PAGE_SIZE_OPTIONS}
            dataShowJumpTo="true"
            dataShowResults={true}
            neonPaginationChange={(newPage, newPageLength) => {
              setCurrentPage(newPage);
              if (newPageLength === undefined) {
                return;
              }
              const next = Number(newPageLength);
              if (Number.isFinite(next) && next > 0 && next !== pageLength) {
                setPageLength(next);
              }
            }}
          />
        )}
        {bodyExtension}
      </div>

      <div data-slot="drawer-button-footer">
        <NeonButtonFooter
          id={`${id}-footer`}
          buttons={[
            {
              id: `${id}-cancel`,
              text: 'Cancel',
              type: NeonButtonTypes.Borderless,
              onClick: onClose,
            },
          ]}
          {...(!effectiveReadOnly
            ? {
                primaryButton: {
                  id: `${id}-save`,
                  text: primaryButtonText,
                  type: NeonButtonTypes.Primary,
                  disabled: primaryDisabled,
                  onClick: handlePrimaryClick,
                },
              }
            : {})}
        />
      </div>
    </NeonModalDrawer>
  );
}
